/** PROGRAMUL DE EXECUȚIE: tipurile, cererile și pregătirea pentru ecran.
 *
 *  Programul îl calculează serviciul de planificare din lista de cantități
 *  (`cost_model.program`, prin `control-plane/api/program.py`), cu setările
 *  proiectului și suprascrierile scenariului. Aici nu se calculează nimic din
 *  program: doar se citește, se pune pe axa timpului și se desenează. */
import { get, post, put } from "../../api";

/** O SUB-FAZĂ a unui pachet, ca fracții din durata lui. Pe un nivel de
 *  structură turnată ciclul e DOUĂ TURNĂRI (motor ≥ 16): verticalele (stâlpii,
 *  pereții: cofrare, armare, turnare), apoi placa (grinzile și placa: la fel),
 *  fiecare fază cu `parte`; apoi decofrarea plăcii (trece de 1: planșeul se
 *  decofrează după ce pachetul s-a încheiat) și decofrarea verticalelor, care
 *  se SUPRAPUNE peste lucrul la placă (`suprapus`) și stă ultima în listă. Pe
 *  terasamente săpătura și fundațiile; altfel «lucru» — fără `parte`.
 *
 *  Contractul listei (`cost_model/program/programare._fazele`): fazele
 *  nesuprapuse sunt în ordinea lui `de_la`, se ating și acoperă [0, 1] cu
 *  lucrul, `pana_la ≥ de_la`; «faza la fracția f» e PRIMA cu f ≤ `pana_la`
 *  (`timelapse/stare.faza`), de aceea cea suprapusă e la coadă, ca să nu
 *  se nimerească în locul fazei de lucru. `start` / `sfarsit` sunt datele ei
 *  calendaristice, scrise de motor (interfața nu are calendarul). Cofrajele
 *  sunt un fapt al programului, timelapse-ul doar le desenează. */
export type FazaPachet = {
  faza: "cofraj" | "armare" | "beton" | "decofrare" | "montaj" | "sapatura" | "fundatii" | "lucru";
  de_la: number; pana_la: number;
  /** turnarea din care face parte faza, numai pe structura turnată */
  parte?: "verticale" | "placa";
  /** se petrece în paralel cu fazele de lucru (o așteptare, nu manoperă) */
  suprapus?: boolean;
  /** date ISO (motor ≥ 16) */
  start?: string; sfarsit?: string;
};
/** Desfășurarea pachetului într-o săptămână calendaristică (de luni): fracția
 *  lui la începutul și la sfârșitul ei. */
export type SaptamanaPachet = { luni: string; de_la: number; pana_la: number };

export type EtapaProgram = {
  /** id stabil `unitate/nivel/etapă` (`C1/3/E30`, `S1/-/E10`, `S/-/E00`; motor ≥ 14) */
  id?: string;
  etapa: string; nume: string; corp: string; nivel: number | null;
  /** zile lucrătoare de la start */
  zi_start: number; zi_sfarsit: number;
  /** date ISO */
  start: string; sfarsit: string;
  zile: number; ore: Record<string, number>; muncitori: Record<string, number>;
  impins_zile: number; ore_macara: number; articole: string[];
  cost_eur?: number;
  /** cantitățile devizului pe pachet (motor ≥ 14) */
  beton_m3?: number; cofraj_m2?: number; otel_kg?: number; sapatura_m3?: number; umplutura_m3?: number;
  faze?: FazaPachet[]; desfasurare?: SaptamanaPachet[];
  /** frontul unei etape de nivel: tronsoanele corpului, pe rând, cu ponderea lor */
  front?: { tronsoane: [string, number][] } | null;
};

export type CicluNivel = {
  zile: number; minim: number; macara: number; echipa: number; urmasi: number; ore_macara: number;
  /** cât ar cere betonul pe zi (pompa sau stația; motor ≥ 4) */
  beton?: number;
  da_ritmul: "minim" | "macara" | "beton" | "echipa" | "urmasi";
  /** regula care a dat ciclul (`RegulaProgram.id`) */
  limitat_de?: string[];
};

export type NivelProgram = {
  nivel: number; nume: string | null; subsol: boolean; arie_m2: number; plafon_oameni: number | null;
  ciclu: CicluNivel | null; etape: EtapaProgram[];
};

/** Macaralele unui corp: stau pe el de la structură până la fațadă (`luni`,
 *  `luni_macara`), dar la plafonul șantierului numără numai cât ține
 *  structura (`structura`); `asteptat_zile` = cât a stat corpul după macarale
 *  libere, `eliberat_de` = corpul a cărui structură gata l-a lăsat să pornească. */
export type MacaraleCorp = {
  buc: number; zi_start: number; zi_sfarsit: number; start: string; sfarsit: string; luni: number; luni_macara: number;
  structura: { zi_start: number; zi_sfarsit: number; start: string; sfarsit: string } | null;
  asteptat_zile: number; plafon_la_start: number | null; eliberat_de: string | null;
  /** subsolul comun pe a cărui placă a pornit corpul, când el i-a dat startul */
  subsol?: string | null;
  /** ce a ținut corpul pe loc: plafonul de macarale, subsolul de sub el */
  limitat_de?: string[];
};

/** O perioadă cu alt plafon de macarale: de aici se «deblochează» un corp în plus. */
export type PerioadaMacarale = { de_la: string; pana_la: string; maxim: number };

/** O regulă de planificare, cu parametrii ei efectivi și explicația în limba
 *  ecranului (`cost_model/program/reguli.py`). */
export type RegulaProgram = {
  id: string; categorie: "lag" | "asteptare" | "capacitate" | "resursa" | "succesiune" | "calendar" | "fereastra" | "logistica";
  activa: boolean; aplica: Record<string, unknown>; sursa: string; parametri: Record<string, unknown>;
  nume: string; explicatie: string;
};

/** O restricție fizică a proiectului (`cost_model/program/restrictii.py`): ce
 *  cere sau folosește proiectul (`valoare`), cât încape (`limita`) și dacă a
 *  fost atinsă — atunci pârghia care s-ar lovi de ea e refuzată cu motivul. */
export type RestrictieProgram = {
  id: string; activa: boolean; valoare: number | null; limita: number | null; um: string;
  sursa: string; atinsa: boolean; unde: string[]; nume: string; explicatie: string; atinsa_text: string | null;
};

/** O pârghie (`cost_model/program/parghii.py`): ce scurtează programul, cu
 *  costul și limita ei; pe un program spune dacă e aplicată, disponibilă sau
 *  refuzată cu motivul. */
export type ParghieProgram = {
  id: string; regula: string; limita: string | null;
  valoare: { tip: "int" | "float"; implicit: number; min: number; max: number } | null;
  nume: string; explicatie: string; cost: string | null; risc: string;
  aplicata?: boolean; valoare_aplicata?: number | null; disponibila?: boolean; motiv_refuz?: string | null;
  limita_neverificata?: boolean;
};

/** Un cost al setărilor active (pârghie sau restricție atinsă), cu formula scrisă. */
export type CostSuplimentar = { id: string; eur: number; cantitate: number; um: string; explicatie: string };

/** O pârghie cerută în setări: id și, unde are, valoarea (o macara în plus = 2, ziua de 10 ore…). */
export type ParghieCeruta = { id: string; valoare?: number | null };

export type CorpProgram = { cod: string; niveluri: NivelProgram[]; etape: EtapaProgram[]; macarale: MacaraleCorp | null };

/** Un subsol comun (S1, S2…): o unitate ca un corp, cu nivelurile lui de
 *  subsol; `corpuri` = blocurile care stau pe el și pornesc după placa lui. */
export type SubsolProgram = CorpProgram & { corpuri: string[] };

export type EtapaRezumat = {
  cod: string; nume: string; unitate: "nivel" | "corp" | "santier"; dupa: string[]; lag_zile: number;
  zi_start: number; zi_sfarsit: number; start: string; sfarsit: string;
  muncitori: Record<string, number>; ore: number; decalaj_final_zile: number | null; unitati: number; ritm: boolean;
};

export type MeserieProgram = {
  nume: string; recomandat: number; folosit: number; ore: number; zile_pe_santier: number; varf: number;
  utilizare_pct: number | null; start: string; sfarsit: string; etape: string[];
};

export type SaptamanaProgram = {
  saptamana: number; start: string; pe_meserie: Record<string, number>; pe_etapa: Record<string, number>; total: number;
  /** zilele lucrătoare ale săptămânii calendaristice (motor ≥ 14) */
  zile_lucratoare?: number;
};

/** Setările programului: ale proiectului (`cost.program`) sau ale unui scenariu
 *  (`scenarii[].program`); ce lipsește ia implicitul motorului. */
export type SetariProgram = Partial<{
  start: string; zile_lucratoare: number[]; sarbatori_ro: boolean; libere: (string | [string, string])[];
  ore_pe_zi: number; productivitate: number; ciclu_min_zl: number; ciclu_min_subsol_zl: number; marja_zl: number;
  ore_macara_m3: number; randament_macara: number; amprenta_pe_macara_m2: number; macarale_max_santier: number;
  macarale_perioade: PerioadaMacarale[]; m2_pe_macara_zi: number;
  material_structura: "beton" | "prefab";
  densitate_m2_om: number; min_om_nivel: number; echipa_min: number;
  zile_min_etapa_zl: number; zile_fundatii_zl: number; muncitori: Record<string, number>; lag_zile: Record<string, number>;
  ordinea_corpurilor: string[];
  /** restricțiile și pârghiile (motor ≥ 4): parametrii regulilor lor și pârghiile cerute */
  pompa_beton: boolean; cofraje_rapide: boolean; lift_santier: boolean; schimburi_structura: 1 | 2;
  beton_statie_m3_zi: number; pompa_m3_zi: number; brat_macara_m: number; distanta_catarge_plus_m: number;
  densitate_min_m2_om: number; densitate_plina_m2_om: number; lift_de_la_etaj: number; curte_min_pct: number;
  parghii: ParghieCeruta[];
  /** sub-fazele nivelului turnat: ponderile pe zile (fără cantități) și, motor ≥ 16, normele de
   *  manoperă și partea verticalelor din fiecare cantitate */
  cofraj_pct: number; armare_pct: number; sapatura_pct: number;
  norma_cofraj_h_m2: number; norma_armare_h_kg: number; norma_beton_h_m3: number;
  parte_verticale_cofraj_pct: number; parte_verticale_otel_pct: number; parte_verticale_beton_pct: number;
}>;

export type Program = {
  versiune: number; setari: SetariProgram & { start: string };
  /** banii în timp: costul direct pe luni calendaristice */
  flux?: { luni: { luna: string; cost_direct_eur: number; fractie: number }[]; total_direct_eur: number };
  start: string; sfarsit: string; zile_lucratoare: number; luni: number;
  corpuri: CorpProgram[]; santier: EtapaProgram[]; etape: EtapaRezumat[]; meserii: Record<string, MeserieProgram>;
  /** subsolurile comune, programate înaintea blocurilor de pe ele (motor ≥ 12) */
  subsoluri?: SubsolProgram[];
  echipa_structurii: Record<string, number>;
  histograma: SaptamanaProgram[]; muncitori_varf: number; muncitori_medie: number;
  macarale: {
    luni_macara: number; max_simultan: number; max_in_structura: number; plafon: number;
    perioade: PerioadaMacarale[]; fronturi_max: number; pe_santier: Record<string, number>;
    pe_corp: Record<string, MacaraleCorp>;
    /** macaralele fiecărui subsol comun, cât ține structura lui (motor ≥ 12) */
    pe_subsol?: Record<string, MacaraleCorp>;
  };
  /** De ce a pornit fiecare corp când a pornit — ce citește asistentul. */
  explicatii: string[];
  avertismente: string[];
  /** regulile cu parametrii efectivi ai programului (motor ≥ 3) */
  reguli?: RegulaProgram[];
  /** restricțiile fizice, pârghiile și costurile a ce e activ (motor ≥ 4) */
  restrictii?: RestrictieProgram[]; restrictii_atinse?: string[]; parghii?: ParghieProgram[];
  costuri_suplimentare?: CostSuplimentar[]; cost_suplimentar_eur?: number;
  cost_macarale?: { eur: number; luni_macara: number; buc: number; pret_luna: number; pret_mobilizare: number };
  manopera_eur?: number;
  /** camioanele taxelor de acces (București A/B), pe etapele fluxurilor lor:
   *  zile-camion și câte camioane pe zi; `null` fără zonă cunoscută */
  camioane?: { zona: string | null; zile_camion: number; varf_pe_zi: number | null;
               pe_etapa: Record<string, { zile_camion: number; zile: number; camioane_pe_zi: number | null }> } | null;
  amprenta_intrari?: string; scenariu?: string | null; durata_s?: number;
};

export type EtapaCatalog = {
  cod: string; nume: string; unitate: "nivel" | "corp" | "santier"; dupa: [string, string][]; lag_zile: number;
  zile_fixe?: number; ritm?: boolean; subsol?: boolean;
};

export type ImpliciteProgram = {
  setari: SetariProgram & { start: string }; etape: EtapaCatalog[]; meserii: Record<string, string>;
  reguli?: RegulaProgram[]; materiale?: string[]; parghii?: ParghieProgram[]; restrictii?: string[];
};

export type SetariProiect = { rev: number; data: SetariProgram; implicite: ImpliciteProgram };

/** Ținta optimizatorului: atâtea luni, cât mai ieftin; sau cel mai bun ROI anual. */
export type TintaOptimizare = { mod: "luni"; luni: number } | { mod: "roi" };

/** Un punct al traiectoriei optimizatorului: programul după `pas` pârghii. */
export type PunctOptimizare = {
  pas: number; parghie: string | null; valoare: number | null;
  d_luni?: number; d_cost?: number; d_roi?: number;
  luni: number; cost_suplimentar_eur: number; cost_macarale_eur: number; cost_extra_eur: number; sfarsit: string;
  restrictii_atinse: string[];
  roi_anual?: number; tic?: number; profit_net?: number; capital_max?: number | null; capital_ani?: number | null; rir?: number | null;
};

/** Ce întoarce optimizatorul (`cost_model/program/optimizare.py`, prin
 *  `POST …/program/optimizeaza`): pașii găsiți, traiectoria întreagă, setările
 *  de pus pe un scenariu, pârghiile refuzate cu motivul, cele fără câștig. */
export type RezultatOptimizare = {
  mod: "luni" | "roi"; tinta_luni: number | null; atins: boolean | null;
  baza: PunctOptimizare; rezultat: PunctOptimizare;
  pasi: { pas: number; parghie: string; valoare: number | null; d_luni: number; d_cost: number; d_roi: number }[];
  traiectorie: PunctOptimizare[];
  setari: SetariProgram; parghii: ParghieProgram[];
  refuzate: { id: string; nume: string; motiv: string }[];
  fara_castig: { id: string; d_luni: number; d_cost: number; d_roi: number }[];
  restrictii: RestrictieProgram[]; restrictii_atinse: string[]; costuri_suplimentare: CostSuplimentar[];
  explicatii: string[]; avertismente: string[]; scenariu?: string | null; note?: string[]; durata_s?: number;
};

// --- API -------------------------------------------------------------------

const cale = (projectId: string) => `/api/projects/${projectId}`;

export const getProgram = (projectId: string, solutionId: string, opt: { scenariu?: string | null; refa?: boolean } = {}) => {
  const q = new URLSearchParams();
  if (opt.scenariu) q.set("scenariu", opt.scenariu);
  if (opt.refa) q.set("refa", "1");
  const s = q.toString();
  return get<Program>(`${cale(projectId)}/solutions/${solutionId}/program${s ? `?${s}` : ""}`);
};

export const getSetariProgram = (projectId: string) => get<SetariProiect>(`${cale(projectId)}/costuri/program/setari`);

export const optimizeazaProgram = (projectId: string, solutionId: string,
                                   body: { tinta: TintaOptimizare; scenariu?: string | null; permise?: string[]; pasi_max?: number }) =>
  post<RezultatOptimizare>(`${cale(projectId)}/solutions/${solutionId}/program/optimizeaza`, body);

export const putSetariProgram = (projectId: string, rev: number, data: SetariProgram) =>
  put<{ rev: number; data: SetariProgram }>(`${cale(projectId)}/costuri/program/setari`, { rev, data });

// --- pe axa timpului ----------------------------------------------------------

/** Culoarea fiecărei etape: structura închisă, meseriile pe tonuri deosebite și
 *  ca luminozitate, lucrările de șantier gri. */
export const CULORI_ETAPE: Record<string, string> = {
  E00: "#6b7380", E10: "#8a6d3b", E20: "#2f4470", E25: "#7c8794", E30: "#3b5b8f", E40: "#a97614", E45: "#5b8aa6",
  E50: "#6b4fa1", E60: "#0b7f92", E65: "#5b8aa6", E70: "#4f8207", E80: "#b5323d", E85: "#7c8794", E90: "#2e6f9e",
  E95: "#8c5a2b", E97: "#6b4fa1", E99: "#6b7380", E100: "#6b7380",
};

export const culoareEtapei = (cod: string) => CULORI_ETAPE[cod] ?? "#6b7380";

export type Segment = {
  etapa: string; nume: string; corp: string; nivel: number | null;
  /** procente din lungimea programului */
  stanga: number; latime: number; culoare: string; e: EtapaProgram;
};

export type RandGantt = { cheie: string; eticheta: string; segmente: Segment[] };

export type GrupGantt = {
  cod: string; eticheta: string;
  /** «S1+P+10» la un corp, «subsol comun · 2 niveluri» la un subsol; la
   *  șantier nimic — rândurile lui se citesc dedesubt */
  regim: string | null; amprenta_m2: number | null; macarale: MacaraleCorp | null;
  /** la un subsol comun: blocurile de pe el */
  pe_el?: string[];
  randuri: RandGantt[];
  /** întinderea grupului, pentru bara lui de rezumat */
  stanga: number; latime: number;
  /** cât ține structura (E20/E30) în grup: partea închisă a barei de rezumat */
  structura: { stanga: number; latime: number } | null;
};

/** Numele unui nivel pe rând: numele arhitecturii, altfel S<n> / P / E<n>. */
export function numeleNivelului(n: { nivel: number; nume: string | null }): string {
  if (n.nume) return n.nume;
  return n.nivel < 0 ? `S${-n.nivel}` : n.nivel === 0 ? "P" : `E${n.nivel}`;
}

/** Numele nivelului sub corpul lui: arhitectura scrie «C1 · etaj 3», iar
 *  rândul stă deja sub «C1» — prefixul s-ar repeta pe fiecare rând. */
export function nivelInCorp(corp: string, nume: string): string {
  const rest = nume.startsWith(corp) ? nume.slice(corp.length).match(/^\s*[·\-–:]\s*(.+)$/) : null;
  return rest ? rest[1] : nume;
}

function segment(e: EtapaProgram, total: number): Segment | null {
  if (e.zi_sfarsit <= e.zi_start || total <= 0) return null;
  return {
    etapa: e.etapa, nume: e.nume, corp: e.corp, nivel: e.nivel,
    stanga: (e.zi_start / total) * 100, latime: ((e.zi_sfarsit - e.zi_start) / total) * 100,
    culoare: culoareEtapei(e.etapa), e,
  };
}

/** Tonul unei sub-faze pe bara pachetului: cofrarea (și săpătura) deschise,
 *  armarea medie, turnarea (și restul) în culoarea plină a etapei — pe o bară
 *  se citește dintr-o privire cât din ciclu e cofraj și cât e beton. */
export type TonFaza = "deschis" | "mediu" | "inchis";
export const TONUL_FAZEI: Record<FazaPachet["faza"], TonFaza> = {
  cofraj: "deschis", sapatura: "deschis", armare: "mediu",
  beton: "inchis", decofrare: "inchis", montaj: "inchis", fundatii: "inchis", lucru: "inchis",
};

export type SubSegment = {
  f: FazaPachet; ton: TonFaza;
  /** procente din bara pachetului (durata lui = 100) */
  stanga: number; latime: number;
  /** aici începe cealaltă turnare (altă `parte` decât sub-segmentul dinainte) */
  despartit: boolean;
};

/** SUB-SEGMENTELE unei bare: fazele de LUCRU ale pachetului (cele din [0, 1];
 *  decofrările, care așteaptă peste 1 sau în paralel cu placa, nu se
 *  desenează pe bară — le spune detaliul), ca procente din bară, cu tonul lor
 *  și cu despărțitor unde începe cealaltă turnare. O bară cu o singură fază
 *  (zidăria, montajul) rămâne plină. */
export function subSegmente(e: EtapaProgram): SubSegment[] {
  const lucru = (e.faze ?? []).filter((f) => !f.suprapus && f.faza !== "decofrare" && f.de_la < 1 && f.pana_la > f.de_la);
  if (lucru.length < 2) return [];
  return lucru.map((f, i) => ({
    f, ton: TONUL_FAZEI[f.faza],
    stanga: f.de_la * 100, latime: (Math.min(f.pana_la, 1) - f.de_la) * 100,
    despartit: i > 0 && f.parte !== lucru[i - 1].parte,
  }));
}

const intindere = (segmente: Segment[]) => {
  if (!segmente.length) return null;
  const a = Math.min(...segmente.map((s) => s.stanga));
  const b = Math.max(...segmente.map((s) => s.stanga + s.latime));
  return { stanga: a, latime: Math.max(b - a, 0) };
};

/** Grupurile graficului: șantierul, subsolurile comune, apoi fiecare corp cu
 *  nivelurile de sus în jos (ca linia de echilibru să se citească ca o
 *  diagonală) și lucrările lui de corp dedesubt. */
export function grupuriGantt(p: Program, nume: { santier: string; nivel: (n: NivelProgram) => string;
                                                  subsol: (niveluri: number) => string }): GrupGantt[] {
  const total = Math.max(p.zile_lucratoare, 1);
  const grup = (g: Omit<GrupGantt, "stanga" | "latime" | "structura">): GrupGantt => {
    const toate = g.randuri.flatMap((r) => r.segmente);
    const tot = intindere(toate) ?? { stanga: 0, latime: 0 };
    return { ...g, ...tot, structura: intindere(toate.filter((s) => s.etapa === "E20" || s.etapa === "E30")) };
  };
  const out: GrupGantt[] = [];
  if (p.santier.length) {
    out.push(grup({
      cod: "S", eticheta: nume.santier, regim: null, amprenta_m2: null, macarale: null,
      randuri: p.santier.map((e) => ({
        cheie: `S|${e.etapa}|${e.nivel ?? ""}`,
        eticheta: e.nivel == null ? e.nume : `${e.nume} · ${numeleNivelului({ nivel: e.nivel, nume: null })}`,
        segmente: [segment(e, total)].filter((s): s is Segment => !!s) })),
    }));
  }
  const randurile = (c: CorpProgram): RandGantt[] => {
    const randuri: RandGantt[] = [...c.niveluri].reverse().map((n) => ({
      cheie: `${c.cod}|${n.nivel}`, eticheta: nivelInCorp(c.cod, nume.nivel(n)),
      segmente: n.etape.map((e) => segment(e, total)).filter((s): s is Segment => !!s),
    }));
    // o lucrare de corp fără zile (etapa n-a avut ce face pe el) nu e un rând
    for (const e of c.etape.filter((e) => e.zi_sfarsit > e.zi_start)) {
      randuri.push({ cheie: `${c.cod}|${e.etapa}`, eticheta: e.nume,
                     segmente: [segment(e, total)].filter((s): s is Segment => !!s) });
    }
    return randuri;
  };
  for (const b of p.subsoluri ?? []) {
    out.push(grup({ cod: b.cod, eticheta: b.cod, regim: nume.subsol(b.niveluri.length),
                    amprenta_m2: b.niveluri[0]?.arie_m2 ?? null, macarale: b.macarale, pe_el: b.corpuri,
                    randuri: randurile(b) }));
  }
  for (const c of p.corpuri) {
    const sup = c.niveluri.filter((n) => !n.subsol).length, sub = c.niveluri.length - sup;
    const amprenta = c.niveluri.find((n) => !n.subsol)?.arie_m2 ?? c.niveluri[0]?.arie_m2 ?? null;
    out.push(grup({ cod: c.cod, eticheta: c.cod, regim: `${sub ? `S${sub}+` : ""}P${sup > 1 ? `+${sup - 1}` : ""}`,
                    amprenta_m2: amprenta, macarale: c.macarale, randuri: randurile(c) }));
  }
  return out;
}

/** Un interval pe axa timpului, în procente din zilele lucrătoare ale programului. */
export type Diviziune = { stanga: number; latime: number; data: Date };

/** Axa timpului: anii, lunile și săptămânile, la procentul din zilele
 *  lucrătoare unde cade prima zi lucrătoare a fiecăreia (din săptămânile
 *  histogramei: o săptămână = 5 zile lucrătoare). Etichetele le alege ecranul,
 *  după câți pixeli are fiecare diviziune. */
export function axaTimpului(p: Program): { ani: Diviziune[]; luni: Diviziune[]; saptamani: Diviziune[] } {
  const total = Math.max(p.zile_lucratoare, 1);
  // săptămânile calendaristice (motor ≥ 14 le dă cu zilele lor lucrătoare; mai
  // vechi, 5 pe săptămână): fiecare începe la procentul zilelor lucrătoare
  // scurse înaintea ei
  let scurse = 0;
  const saptamani: Diviziune[] = p.histograma.map((s) => {
    const d: Diviziune = { stanga: Math.min((scurse / total) * 100, 100), latime: 0, data: new Date(s.start + "T00:00:00") };
    scurse += s.zile_lucratoare ?? 5;
    return d;
  });
  const latimi = (l: Diviziune[]) => l.map((d, i) => ({ ...d, latime: (l[i + 1]?.stanga ?? 100) - d.stanga }));
  const primele = (cheie: (d: Date) => string) => saptamani.filter((s, i) => i === 0 || cheie(s.data) !== cheie(saptamani[i - 1].data));
  return {
    ani: latimi(primele((d) => `${d.getFullYear()}`)),
    luni: latimi(primele((d) => `${d.getFullYear()}-${d.getMonth()}`)),
    saptamani: latimi(saptamani),
  };
}

/** Histograma pe săptămâni, ca înălțimi relative pe etape (în ordinea etapelor). */
export function bareHistograma(p: Program, ordine: string[]): { total: number; parti: { etapa: string; h: number; culoare: string }[]; start: string }[] {
  const max = Math.max(...p.histograma.map((s) => s.total), 1);
  return p.histograma.map((s) => ({
    total: s.total, start: s.start,
    parti: ordine.filter((e) => s.pe_etapa[e]).map((e) => ({ etapa: e, h: (s.pe_etapa[e] / max) * 100, culoare: culoareEtapei(e) })),
  }));
}

/** Starea unei meserii, pentru cip: cât din timpul pe șantier chiar lucrează. */
export function stareaMeseriei(m: MeserieProgram, impusa: boolean): "impusa" | "la_ritm" | "standby" | "putin" {
  if (impusa) return "impusa";
  if (m.utilizare_pct == null) return "la_ritm";
  if (m.utilizare_pct >= 70) return "la_ritm";
  if (m.utilizare_pct >= 40) return "standby";
  return "putin";
}

/** Suprascrierile de program ale unui scenariu, adunate cu cele ale omului de
 *  pe ecran: o meserie pusă la efectivul recomandat iese din suprascrieri. */
export function cuMuncitorii(baza: SetariProgram | null | undefined, folosit: Record<string, number>,
                             recomandat: Record<string, number>): SetariProgram {
  const muncitori: Record<string, number> = { ...(baza?.muncitori ?? {}) };
  for (const [m, n] of Object.entries(folosit)) {
    if (n > 0 && n !== recomandat[m]) muncitori[m] = n; else delete muncitori[m];
  }
  const out: SetariProgram = { ...(baza ?? {}) };
  if (Object.keys(muncitori).length) out.muncitori = muncitori; else delete out.muncitori;
  return out;
}

export const areProgram = (s: SetariProgram | null | undefined) => !!s && Object.keys(s).length > 0;

/** Suprascrierile de program ale unui scenariu cu o pârghie în plus: o pârghie
 *  repetabilă (o macara în plus) își adună valoarea, celelalte stau o singură dată. */
export function cuParghia(baza: SetariProgram | null | undefined, p: ParghieProgram, valoare?: number | null): SetariProgram {
  const v = valoare ?? p.valoare?.implicit ?? null;
  const parghii = [...(baza?.parghii ?? [])];
  const i = parghii.findIndex((x) => x.id === p.id);
  if (i >= 0 && p.id === "macarale_plus") parghii[i] = { id: p.id, valoare: (parghii[i].valoare ?? 1) + (v ?? 1) };
  else if (i < 0) parghii.push(v == null ? { id: p.id } : { id: p.id, valoare: v });
  return { ...(baza ?? {}), parghii };
}
